import React, { useEffect } from "react";
import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import { Play, Radio, Video, Tv2, ChevronRight, Check, Sparkles } from "lucide-react";
import { LANDING_BY_SLUG, LANDING_PAGES } from "../lib/landingPages";

function setMeta(name, value, attr = "name") {
  if (!value) return;
  let m = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute(attr, name);
    document.head.appendChild(m);
  }
  m.setAttribute("content", value);
}

/**
 * Renders one of the 50 SEO landing pages defined in `/lib/landingPages.js`.
 * Unique H1, title, meta description, and canonical per slug. Every page funnels
 * to the home app with 3+ CTAs.
 */
export default function LandingPage() {
  const params = useParams();
  const location = useLocation();
  // Each landing route is mounted at `/${slug}` (no `:slug` placeholder), so
  // `useParams` returns {}. Derive the slug from the current pathname as a
  // fallback so all 50 SEO routes resolve correctly.
  const slug = params.slug || (location.pathname || "").replace(/^\/+/, "").split("/")[0];
  const page = LANDING_BY_SLUG[slug];

  useEffect(() => {
    if (!page) return;
    document.title = page.title;
    setMeta("description", page.description);
    setMeta("keywords", `${page.keyword}, Tap In TV, TapInTVNetwork, streaming platform, independent creators, watch online`);
    setMeta("og:title", page.title, "property");
    setMeta("og:description", page.description, "property");
    setMeta("og:url", `${window.location.origin}/${slug}`, "property");
    setMeta("twitter:title", page.title);
    setMeta("twitter:description", page.description);
    // Canonical
    let c = document.head.querySelector("link[rel='canonical']");
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", `${window.location.origin}/${slug}`);
  }, [page, slug]);

  if (!page) return <Navigate to="/" replace />;

  // Pick 3 related pages (different keyword, same category where possible)
  const related = LANDING_PAGES.filter((p) => p.slug !== page.slug).slice(0, 6);

  return (
    <div data-testid={`landing-${page.slug}`} className="pt-32 pb-24 px-4 sm:px-8 md:px-12 max-w-6xl mx-auto">
      {/* Hero */}
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Tap IN TV Network</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white mt-3 tracking-tighter max-w-4xl mx-auto leading-[0.95]">
          {page.h1}
        </h1>
        <p className="text-white/70 mt-5 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
          {page.description}
        </p>

        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to={page.primary.href}
            data-testid={`landing-cta-primary`}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 shadow-lg shadow-tv-accent/30"
          >
            <Play className="w-4 h-4 fill-current" /> {page.primary.label}
          </Link>
          {page.secondary && (
            <Link
              to={page.secondary.href}
              data-testid={`landing-cta-secondary`}
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
            >
              {page.secondary.label} <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Proof strip */}
      <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { num: "1,000+", label: "Independent titles" },
          { num: "24/7",    label: "Live channels" },
          { num: "$50K",    label: "Creator Awards / season" },
          { num: "100+",    label: "Active community members" },
        ].map((s) => (
          <div key={s.label} className="tv-glass rounded-xl border border-white/10 px-4 py-5 text-center">
            <div className="font-display font-black text-2xl text-tv-accent">{s.num}</div>
            <div className="text-white/60 text-xs mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Features */}
      <div className="mt-14 grid md:grid-cols-3 gap-5">
        <FeatureCard icon={Radio} title="Live Streaming"
          body="HD camera + mic broadcasts with near-zero latency. Multi-guest, waiting room, live chat, tips, emotes." />
        <FeatureCard icon={Video} title="Creator Studio"
          body="Upload videos, schedule drops, gate PPV events, sell merch, and track revenue from one dashboard." />
        <FeatureCard icon={Tv2} title="Everywhere You Watch"
          body="Mobile app, smart TV apps, browser, PWA with offline favorites. Add Tap IN TV to your home screen." />
      </div>

      {/* Back to home band */}
      <div className="mt-14 tv-glass rounded-2xl border border-white/10 p-8 text-center">
        <Sparkles className="w-7 h-7 text-tv-accent mx-auto" />
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-3 tracking-tight">
          Ready to tune in?
        </h2>
        <p className="text-white/70 mt-2 max-w-xl mx-auto text-sm">
          Create a free Tap IN TV account, follow the creators you love, and pick up where you left off on any device.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/" data-testid="landing-cta-home" className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full">
            Go to Tap IN TV Home
          </Link>
          <Link to="/signup" className="text-white/80 hover:text-white text-sm font-semibold px-4 py-3">
            Create a free account →
          </Link>
        </div>
      </div>

      {/* Related */}
      <div className="mt-14">
        <h3 className="font-display font-bold text-xl text-white tracking-tight">Related on Tap IN TV</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/${r.slug}`}
              className="group tv-glass rounded-xl border border-white/10 px-4 py-3 flex items-center gap-3 hover:border-tv-accent/40"
            >
              <Check className="w-4 h-4 text-tv-accent shrink-0" />
              <span className="text-white/90 text-sm group-hover:text-white">{r.h1.split(" — ")[0]}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="tv-glass rounded-xl border border-white/10 p-5">
      <Icon className="w-6 h-6 text-tv-accent" />
      <h3 className="font-display font-bold text-lg text-white mt-3 tracking-tight">{title}</h3>
      <p className="text-white/65 text-sm mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
